//REACT
import { useState, useRef, useEffect } from 'react';

// REDUX
import { useSelector } from 'react-redux';

// AXIOS
import axios from 'axios';

// UTILS
import makeAIChatSessionId from '../../../../../utils/n8n/makeAIChatSessionId';
import getN8NChatStore from '../../../../../utils/n8n/getN8NChatStore';
import renderFormattedText from '../../../../../utils/n8n/renderFormattedText';

// COMPONENTS
import { CloseRounded, SendRounded } from '@mui/icons-material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { IconButton, Typography } from '@mui/material';
import FontAwesomeIcon from '../../../../../components/common/FontAwesomeIcon';
import Text from '../../../../../components/common/Text';
import CommonButton from '../../../../../components/common/CommonButton';

// STYLES
import {
    ChatContainer,
    Overlay,
    ChatHeader,
    HeaderLeft,
    AvatarImage,
    HeaderInfo,
    HeaderActions,
    MessagesContainer,
    MessageBubble,
    MessageText,
    UrlButton,
    MessageTime,
    TypingIndicator,
    TypingDot,
    PredefinedOptionsContainer,
    PredefinedOptionButton,
    InputContainer,
    MessageInput,
    FooterContainer,
    ChatTriggerAlt,
    SendButton,
    TypingText,
    IndicatorWrapper,
} from './styles/N8NChatsStyles';

const BUTTON_TYPES = {
    SCHEDULE: 'schedule',
    DOWNLOADABLE: 'downloadable',
};

const getButtonIcon = (type) => {
    switch (type) {
        case BUTTON_TYPES.SCHEDULE:
            return 'fa-light fa-calendar-day';
        case BUTTON_TYPES.DOWNLOADABLE:
            return 'fa-light fa-arrow-down';
        default:
            return 'fa-light fa-external-link';
    }
};

const CorrectionFeedbackAIModal = (props) => {
    const {
        workflowUrl,
        additionalData,
        predefinedMessages = [],
        sendInitialRequestOnOpen = true,
        initialMessage = '',
        authToken: authTokenProp,
        triggerFaIcon,
        buttonText,
        customTriggerStyles,
        sharedKey,
        behaviourAction,
        behaviourDetail,
    } = props;

    const user = useSelector((state) => state.auth.user);
    const tokenFromRedux = useSelector(
        (state) => state.auth.authToken || state.auth.token
    );
    const authToken =
        authTokenProp || tokenFromRedux || localStorage.getItem('token');

    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPredefinedOption, setSelectedPredefinedOption] =
        useState(null);
    const savedMessageRef = useRef('');
    const messagesEndRef = useRef(null);
    const [sessionId, setSessionId] = useState(null);
    const instanceIdRef = useRef(Math.random().toString(36).slice(2));
    const behaviourSentRef = useRef(false);

    const sharedStore = getN8NChatStore(sharedKey);

    useEffect(() => {
        if (sharedStore) {
            setIsOpen(sharedStore.isOpen);
            setIsMinimized(sharedStore.isMinimized);
            setMessages(sharedStore.messages || []);
            setSessionId(sharedStore.sessionId || null);
            behaviourSentRef.current = sharedStore.behaviourSent || false;
        }
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const send = async (text, addUserBubble = false, sidOverride) => {
        if (isLoading) return;
        const value = (text || '').trim();
        if (addUserBubble && value) {
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now(),
                    text: value,
                    sender: 'user',
                    timestamp: new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                },
            ]);
        }
        setMessage('');
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('chatInput', value);
            formData.append(
                'alumno',
                user?.name.split(' ')[0] || user?.personal_email || 'Usuario'
            );
            formData.append('session_id', sidOverride || sessionId || '');
            if (additionalData && typeof additionalData === 'object') {
                Object.entries(additionalData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null)
                        formData.append(key, String(value));
                });
            }
            const response = await axios.post(workflowUrl, formData);

            const responseDataRaw = response.data || response;

            const responseData = Array.isArray(responseDataRaw)
                ? responseDataRaw[0]
                : responseDataRaw;

            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now() + 1,
                    text:
                        responseData?.output ||
                        'Gracias por tu mensaje. Tu consulta ha sido recibida y será procesada.',
                    sender: 'bot',
                    buttons: responseData?.buttons || [],
                    timestamp: new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                },
            ]);
        } catch (error) {
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now() + 1,
                    text: 'Lo siento, ha ocurrido un error. Por favor intenta nuevamente.',
                    sender: 'bot',
                    timestamp: new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (sharedStore) {
            sharedStore.messages = messages;
        }
    }, [messages]);
    useEffect(() => {
        if (sharedStore) {
            sharedStore.isMinimized = isMinimized;
        }
    }, [isMinimized]);

    const toggleChat = () => {
        const anyOpen = sharedStore ? sharedStore.isOpen : isOpen;
        const next = !anyOpen;

        setIsOpen(next);
        setIsMinimized(false);

        if (sharedStore) {
            sharedStore.isOpen = next;
            sharedStore.activeInstanceId = next ? instanceIdRef.current : null;
        }

        if (next) {
            // Abre el chat
            // Reutiliza sesión si ya existe
            let sid = sharedStore ? sharedStore.sessionId : sessionId;
            if (!sid) {
                sid = makeAIChatSessionId();
                if (sharedStore) sharedStore.sessionId = sid;
                setSessionId(sid);
            } else {
                setSessionId(sid);
            }

            // Enviar evento de comportamiento solo la primera vez
            const alreadySent = sharedStore
                ? sharedStore.behaviourSent
                : behaviourSentRef.current;

            if (!alreadySent && behaviourAction && behaviourDetail) {
                // Marcar como enviado
                if (sharedStore) {
                    sharedStore.behaviourSent = true;
                }
                behaviourSentRef.current = true;

                // Enviar a la API de comportamiento
                fetch('https://behaviourapi.aden.org/mvp_log/log', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'api-key': '4ba4dc27-f800-44f3-8576-bbb42826d8f4',
                    },
                    body: JSON.stringify({
                        site: window.location.href,
                        action: behaviourAction,
                        detail: behaviourDetail,
                        email: window.personal_email,
                    }),
                })
                    .then((response) => response.json())
                    .then((data) => console.info('Behaviour logged:', data))
                    .catch((error) => console.error('Behaviour error:', error));
            }

            // Si ya hay mensajes en store, no enviar inicial
            const hasMessages = sharedStore
                ? (sharedStore.messages || []).length > 0
                : messages.length > 0;

            if (sendInitialRequestOnOpen && !isLoading && !hasMessages) {
                send(initialMessage, false, sid);
            } else if (sharedStore && sharedStore.messages) {
                setMessages(sharedStore.messages);
            }
        } else {
            // Cierra el chat
            // Si se usa sharedKey, no limpiar la sesión para mantener continuidad
            if (!sharedStore) {
                setSessionId(null);
            }
        }
    };

    const handlePredefinedOptionClick = (index) => {
        if (selectedPredefinedOption === index) {
            setSelectedPredefinedOption(null);
            setMessage(savedMessageRef.current);
        } else {
            savedMessageRef.current = message;
            setSelectedPredefinedOption(index);
            setMessage('');
        }
    };

    const minimizeChat = () => {
        setIsMinimized(!isMinimized);
        if (sharedStore) {
            sharedStore.isMinimized = !isMinimized;
        }
    };

    const handleSendMessage = async () => {
        const textToSend =
            selectedPredefinedOption !== null
                ? predefinedMessages[selectedPredefinedOption]
                : message.trim();

        if (!textToSend || isLoading) return;

        await send(textToSend, true);

        // Limpiar estado después de enviar
        setMessage('');
        setSelectedPredefinedOption(null);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !selectedPredefinedOption) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const canGenerate =
        (message.trim() || selectedPredefinedOption !== null) && !isLoading;

    const handleOpenUrl = (url) => {
        // Enviar a la API de comportamiento
        fetch('https://behaviourapi.aden.org/mvp_log/log', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'api-key': '4ba4dc27-f800-44f3-8576-bbb42826d8f4',
            },
            body: JSON.stringify({
                site: window.location.href,
                action: 'correcciones_ia_agendar_cita',
                detail: behaviourDetail,
                email: window.personal_email,
            }),
        })
            .then((response) => response.json())
            .then((data) => console.info('Behaviour logged:', data))
            .catch((error) => console.error('Behaviour error:', error));

        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <ChatTriggerAlt
                onClick={toggleChat}
                aria-label="Abrir chat"
                customStyles={customTriggerStyles}
                data-behaviour-action={behaviourAction ?? ''}
                data-behaviour-detail={behaviourDetail ?? ''}
            >
                <FontAwesomeIcon icon={triggerFaIcon} />
                {buttonText && (
                    <Text fontSize={customTriggerStyles?.fontSize}>
                        {buttonText}
                    </Text>
                )}
            </ChatTriggerAlt>
            {isOpen && (
                <>
                    <Overlay onClick={toggleChat} />
                    <ChatContainer isMinimized={isMinimized}>
                        <ChatHeader>
                            <HeaderLeft>
                                <AvatarImage>
                                    <img
                                        src="/assets/adi/adi-avatar.svg"
                                        alt="ADI"
                                    />
                                </AvatarImage>
                                <HeaderInfo>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 600, color: '#fff' }}
                                    >
                                        ADI
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#ffff' }}
                                    >
                                        Asistente virtual con inteligencia
                                        artificial
                                    </Typography>
                                </HeaderInfo>
                            </HeaderLeft>
                            <HeaderActions>
                                <IconButton
                                    onClick={toggleChat}
                                    size="small"
                                    sx={{ color: '#fff' }}
                                >
                                    <CloseRounded fontSize="small" />
                                </IconButton>
                            </HeaderActions>
                        </ChatHeader>

                        {!isMinimized && (
                            <>
                                <MessagesContainer>
                                    {messages.map((msg) => (
                                        <MessageBubble
                                            key={msg.id}
                                            isUser={msg.sender === 'user'}
                                        >
                                            <MessageText
                                                isUser={msg.sender === 'user'}
                                            >
                                                {renderFormattedText(msg.text)}
                                            </MessageText>
                                            {msg.sender === 'bot' &&
                                                msg.buttons &&
                                                Array.isArray(msg.buttons) &&
                                                msg.buttons.length > 0 &&
                                                msg.buttons.map(
                                                    (button, btnIndex) =>
                                                        button.url &&
                                                        button.url.trim() !==
                                                            '' && (
                                                            <UrlButton
                                                                key={`${msg.id}-btn-${btnIndex}`}
                                                                onClick={() =>
                                                                    handleOpenUrl(
                                                                        button.url
                                                                    )
                                                                }
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={getButtonIcon(
                                                                        button.type
                                                                    )}
                                                                />
                                                                {button.label ||
                                                                    'Abrir enlace'}
                                                            </UrlButton>
                                                        )
                                                )}
                                            <MessageTime>
                                                {msg.timestamp}
                                            </MessageTime>
                                        </MessageBubble>
                                    ))}
                                    {isLoading && (
                                        <MessageBubble isUser={false}>
                                            <TypingIndicator>
                                                <TypingText
                                                    variant="body2"
                                                    color="#616161"
                                                    lineHeight="10px"
                                                >
                                                    Recuperando información de
                                                    la corrección
                                                </TypingText>
                                                <IndicatorWrapper>
                                                    <TypingDot />
                                                    <TypingDot />
                                                    <TypingDot />
                                                </IndicatorWrapper>
                                            </TypingIndicator>
                                        </MessageBubble>
                                    )}
                                    <div ref={messagesEndRef} />
                                </MessagesContainer>
                                {Array.isArray(predefinedMessages) &&
                                    predefinedMessages.length > 0 && (
                                        <PredefinedOptionsContainer>
                                            {predefinedMessages.map(
                                                (text, index) => (
                                                    <PredefinedOptionButton
                                                        key={`${index}-${text}`}
                                                        onClick={() =>
                                                            handlePredefinedOptionClick(
                                                                index
                                                            )
                                                        }
                                                        disabled={isLoading}
                                                        isSelected={
                                                            selectedPredefinedOption ===
                                                            index
                                                        }
                                                    >
                                                        {text}
                                                    </PredefinedOptionButton>
                                                )
                                            )}
                                        </PredefinedOptionsContainer>
                                    )}
                                <FooterContainer>
                                    <InputContainer>
                                        <MessageInput
                                            placeholder={
                                                selectedPredefinedOption !==
                                                null
                                                    ? 'Opción seleccionada. Haz clic en "Generar" para enviar.'
                                                    : 'Escribe tu mensaje aquí...'
                                            }
                                            multiline
                                            maxRows={3}
                                            value={message}
                                            onChange={(e) =>
                                                setMessage(e.target.value)
                                            }
                                            onKeyPress={handleKeyPress}
                                            disabled={
                                                isLoading ||
                                                selectedPredefinedOption !==
                                                    null
                                            }
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                        {/* <IconButton
                                            onClick={handleSendMessage}
                                            disabled={!canGenerate}
                                        >
                                            <FontAwesomeIcon icon="fa-light fa-arrow-right" />
                                        </IconButton>
                                        <CommonButton
                                            variant="filled"
                                            label="Generar"
                                            Icon={() => (
                                                <Icon
                                                    icon="mdi:sparkles"
                                                    width="24px"
                                                    height="24px"
                                                />
                                            )}
                                            onClick={handleSendMessage}
                                            disabled={!canGenerate}
                                        /> */}
                                        <SendButton
                                            onClick={handleSendMessage}
                                            disabled={!canGenerate}
                                            color="primary"
                                        >
                                            <SendRounded />
                                        </SendButton>
                                    </InputContainer>
                                </FooterContainer>
                            </>
                        )}
                    </ChatContainer>
                </>
            )}
        </>
    );
};

export default CorrectionFeedbackAIModal;
